import { IProfilingCode } from 'legacy/lut/classes/models/profiling/codes'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { PartnerFlagRequestStatus } from 'clickNgo/configs/profiling/partnerFlag'
import { checkForInactivePartnerFlags } from 'clickNgo/entities/partner/api/partnerFlags'
import {
  IPartnerFlag,
  PartnerFlagRequestAction,
} from 'clickNgo/entities/partner/models/partnerFlag'
import { fetchAvailableProfilingCodes } from 'clickNgo/entities/profilingCode/api'
import { useCheckSuperAdminAccess } from 'clickNgo/hooks/access'
import { Nullable } from 'cng-common/models/utils'
import { selectUser } from 'core-components/sitebuilder/store/selectors/selectUser'

import { getDefaultEndDate, getDefaultStartDate, PartnerFlagWarningConfig } from '../config/configs'
import { filterDsbProgramCodeByPartnerFlags } from '../lib/filterDsbProgramCodeByPartnerFlags'
import { getPartnerFlagWarningConfig } from '../lib/getPartnerFlagWarningConfig'
import { getProfilingCodeSource } from '../lib/getProfilingCodeSource'

import { IPartnerFlagRequestProps } from './types'

const getStartOfDay = (date: Date | null) => {
  if (!date) {
    return null
  }

  return new Date(date).setHours(0, 0, 0, 0)
}

type PartnerFlagRequestModelProps = Omit<IPartnerFlagRequestProps, 'onClose'>

export const usePartnerFlagRequest = ({
  partner,
  partnerFlags,
  type,
  onApply,
}: PartnerFlagRequestModelProps) => {
  const [profilingCodes, setProfilingCodes] = useState<IProfilingCode[]>([])
  const { email: userEmail, roleIds: userRoles } = useSelector(selectUser)
  const isSuperAdmin = useCheckSuperAdminAccess()

  const [selectedProfilingCode, setSelectedProfilingCode] = useState<Nullable<IProfilingCode>>(null)
  const [inactivePartnerFlag, setInactivePartnerFlag] = useState<Nullable<IPartnerFlag>>(null)
  const [warningConfig, setWarningConfig] = useState<Nullable<PartnerFlagWarningConfig>>(null)
  const [isWarningVisible, setIsWarningVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [startDate, setStartDate] = useState(getDefaultStartDate)
  const [endDate, setEndDate] = useState(getDefaultEndDate)

  const source = useMemo(
    () =>
      getProfilingCodeSource({
        profilingCodes,
        userEmail,
        userRoles,
        partnerCountry: partner.country,
      }),
    [profilingCodes, userEmail, userRoles, partner.country]
  )

  const startDay = getStartOfDay(startDate)
  const endDay = getStartOfDay(endDate)
  const todayDay = getStartOfDay(getDefaultStartDate())

  const isStartDateMoreThanEndDate =
    startDay !== null && endDay !== null ? startDay >= endDay : false

  const isStartDateLessThanCurrent =
    startDay !== null && todayDay !== null ? startDay < todayDay : false

  const hasRequiredRole = useMemo(() => {
    if (isSuperAdmin) {
      return true
    }

    if (
      selectedProfilingCode !== null &&
      selectedProfilingCode.roles.some((role) => userRoles.includes(role))
    ) {
      return true
    }

    return false
  }, [isSuperAdmin, selectedProfilingCode, userRoles])

  const isRequiredUser = useMemo(() => {
    if (isSuperAdmin) {
      return true
    }

    if (selectedProfilingCode !== null && selectedProfilingCode.users.includes(userEmail)) {
      return true
    }

    return false
  }, [isSuperAdmin, selectedProfilingCode, userEmail])

  const isSelectedCodeAvailable = useMemo(
    () => selectedProfilingCode !== null && (hasRequiredRole || isRequiredUser),
    [hasRequiredRole, isRequiredUser, selectedProfilingCode]
  )

  const isApplyButtonDisabled = useMemo(
    () => !isSelectedCodeAvailable || isStartDateMoreThanEndDate || isStartDateLessThanCurrent,
    [isSelectedCodeAvailable, isStartDateLessThanCurrent, isStartDateMoreThanEndDate]
  )

  const onProfilingCodeSelect = useCallback(
    async (_: string, item?: IProfilingCode) => {
      if (!item) {
        return
      }

      setIsLoading(true)
      try {
        const selectedWarningConfig = getPartnerFlagWarningConfig(item.code)

        const [inactiveFlag = null] = await checkForInactivePartnerFlags({
          partnerId: partner.partnerId,
          partnerFlag: item,
        })

        setInactivePartnerFlag(inactiveFlag)
        setSelectedProfilingCode(item)
        setEndDate(getDefaultEndDate())
        setStartDate(getDefaultStartDate())
        setWarningConfig(selectedWarningConfig)
        setIsWarningVisible(Boolean(selectedWarningConfig))
      } finally {
        setIsLoading(false)
      }
    },
    [partner.partnerId]
  )

  const onApplyClickHandler = useCallback(() => {
    if (selectedProfilingCode && partner) {
      const action =
        inactivePartnerFlag === null
          ? PartnerFlagRequestAction.Add
          : PartnerFlagRequestAction.Modify

      const partnerFlagRequest: IPartnerFlag = {
        action,
        code: selectedProfilingCode.code,
        codeName: selectedProfilingCode.codeName,
        endDate,
        startDate,
        partnerId: partner.partnerId,
        status: PartnerFlagRequestStatus.New,
        codeType: type,
        caption: selectedProfilingCode.codeName,
        country: partner.country,
        market: partner.market,
        entryId: '',
      }

      onApply(partnerFlagRequest)
    }
  }, [endDate, onApply, partner, selectedProfilingCode, startDate, type, inactivePartnerFlag])

  const hasWarningCancel = Boolean(warningConfig?.cancelBtnText)

  const handleWarningClose = useCallback(() => {
    setIsWarningVisible(false)
  }, [])

  const handleWarningCancel = useCallback(() => {
    setIsWarningVisible(false)
    setWarningConfig(null)
    setSelectedProfilingCode(null)
    setInactivePartnerFlag(null)
    setEndDate(getDefaultEndDate())
    setStartDate(getDefaultStartDate())
  }, [])

  useEffect(() => {
    if (partner && partner.market) {
      const currentTypePartnerFlags = partnerFlags.filter(({ codeType }) => codeType === type)

      setIsLoading(true)
      fetchAvailableProfilingCodes({
        partnerFlags: currentTypePartnerFlags,
        type,
        market: partner.market,
      })
        .then((result) => {
          setProfilingCodes(
            filterDsbProgramCodeByPartnerFlags({
              partnerFlags,
              profilingCodes: result,
              type,
            })
          )
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }, [partner, partnerFlags, type])

  return {
    endDate,
    handleWarningCancel,
    handleWarningClose,
    hasRequiredRole,
    hasWarningCancel,
    inactivePartnerFlag,
    isApplyButtonDisabled,
    isLoading,
    isRequiredUser,
    isSelectedCodeAvailable,
    isStartDateLessThanCurrent,
    isStartDateMoreThanEndDate,
    isWarningVisible,
    onApplyClickHandler,
    onProfilingCodeSelect,
    selectedProfilingCode,
    setEndDate,
    setStartDate,
    source,
    startDate,
    userEmail,
    userRoles,
    warningConfig,
  }
}
